import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { AuthShell } from "@/pages/Login";
import { api, apiError, naira } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying");
  const [amount, setAmount] = useState(null);
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const reference = params.get("reference") || params.get("trxref");
    if (!reference) {
      setState("failed");
      return;
    }
    api
      .post(`/api/escrow/payments/verify/${encodeURIComponent(reference)}`)
      .then(({ data }) => {
        if (data.success) {
          setAmount(data.amount_ngn);
          setState("funded");
          toast.success("Payment received", { description: "Your money is held safely until you confirm the visit." });
        } else {
          setState("failed");
          toast.error("That payment did not complete");
        }
      })
      .catch((e) => {
        setState("failed");
        toast.error(apiError(e));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthShell
      title={state === "funded" ? "Payment received" : state === "failed" ? "Payment not completed" : "Confirming your payment"}
      subtitle={
        state === "funded"
          ? "The money is held in escrow until the visit is confirmed."
          : state === "failed"
          ? "No money has left escrow. You can try funding the booking again."
          : "Please wait while we confirm this with Paystack…"
      }
    >
      <div className="flex flex-col items-center gap-5 py-4" data-testid={`payment-callback-${state}`}>
        {state === "verifying" && <Loader2 className="h-7 w-7 animate-spin text-emerald-800" />}
        {state === "funded" && (
          <>
            <CheckCircle2 className="h-9 w-9 text-emerald-700" />
            {amount != null && (
              <p className="font-display text-2xl font-bold text-slate-900" data-testid="payment-callback-amount">
                {naira(amount)}
              </p>
            )}
          </>
        )}
        {state === "failed" && <XCircle className="h-9 w-9 text-red-600" />}
        {state !== "verifying" && (
          <Button
            className="w-full rounded-full bg-emerald-900 py-6 hover:bg-emerald-800"
            onClick={() => navigate("/bookings", { replace: true })}
            data-testid="payment-callback-bookings-btn"
          >
            Back to my bookings
          </Button>
        )}
      </div>
    </AuthShell>
  );
}
